import { type CardTabListType } from 'antd/lib/card';
import i18n from 'i18next';

import { getTableColumnFilterValues } from 'components/data/helpers/tableFilterUtils';
import { type PresetFilterType } from 'components/DataTable';
import { type TQueryFilter, type TQuerySort } from 'components/DataTable/types';
import {
  getShownColumns,
  isHiddenColumn,
  resolveSortBy,
  tableStorage,
} from 'components/DataTable/utils';
import {
  SORT_BY_REPORT_ARG_KEY,
  VISIBLE_COLUMNS_REPORT_ARG_KEY,
} from 'components/ExportReport/utils';
import {
  type AtlasGql_DamageSchemaAttributeSelection,
  AtlasGqlConfirmationStatus,
  AtlasGqlFilterOperator,
} from 'types/atlas-graphql';

export const SITE_DAMAGE_LIST_TABLE_ID = 'site-damage-list-table2';
export const DAMAGE_LIST_TABLE_ID = 'damage-list-table2';

const SEARCHABLE_SCHEMA_FILTER_KEYS = new Set(['Component', 'Material', 'Type', 'Subtype']);

/**
 * Universal-filter keys that already scope Damages 2.0 (site/turbine pages, ids, etc.).
 * Must match generateDamageTableFilters (`site`, not `siteId`). schemaId is not scoping.
 */
const DAMAGES_2_SCOPING_UNIVERSAL_FILTER_KEYS = [
  'id',
  'assetId',
  'assetType',
  'site',
  'turbineId',
  'inspectionId',
  'critical',
] as const;

export const hasScopingUniversalFilters = (
  universalFilters: ReadonlyArray<Pick<TQueryFilter, 'key'>>
): boolean =>
  universalFilters.some(filter =>
    (DAMAGES_2_SCOPING_UNIVERSAL_FILTER_KEYS as readonly string[]).includes(filter.key)
  );

// A column filter with the same key and operator is dropped in favor of the universal one
// (see `resolveFilterBy`), so those columns show locked.
export const getLockedFilters = (universalFilter: TQueryFilter[] = []): Map<string, string[]> =>
  new Map(
    universalFilter
      .filter(({ operator }) => operator === AtlasGqlFilterOperator.Equals)
      .map(({ key, values }) => [key, values])
  );

export type DamageSchemaFilterColumn = {
  key?: string | number;
  type?: string;
  config?: Record<string, unknown>;
};

type GetDamageSchemaFilterStateParams = {
  columns: DamageSchemaFilterColumn[];
  currentColumnKey: string;
  filteredInfo?: Record<string, unknown>;
};

type DamageSchemaFilterState = {
  hasMultipleSelections: boolean;
  selections: AtlasGql_DamageSchemaAttributeSelection[];
};

type GetDamageSchemaPrefetchSelectionsParams = {
  columns: DamageSchemaFilterColumn[];
  filteredInfo?: Record<string, unknown>;
};

const getSelectFilterValues = (
  filteredInfo: Record<string, unknown> | undefined,
  columnKey: string
): string[] => getTableColumnFilterValues(filteredInfo, columnKey);

const normalizeDamageSchemaStringValue = (value: string): string => (value === ' ' ? '' : value);

export const getSelectedDamageSchemaFilterValues = (selectedKeys: unknown[]): string[] =>
  getSelectFilterValues({ selected: selectedKeys }, 'selected').map(
    normalizeDamageSchemaStringValue
  );

export const getDamageSchemaFilterState = ({
  columns,
  currentColumnKey,
  filteredInfo,
}: GetDamageSchemaFilterStateParams): DamageSchemaFilterState => {
  const selectColumnKeys = Array.from(
    new Set(
      columns
        .filter(({ type, key }) => type === 'string' && typeof key === 'string')
        .map(({ key }) => key as string)
    )
  );

  const selections: AtlasGql_DamageSchemaAttributeSelection[] = [];

  for (const columnKey of selectColumnKeys) {
    // Open-column multi-select does not affect query variables (this column is never
    // sent). Only other applied multi-selects block suggestions — the API accepts one
    // string per attribute.
    if (columnKey === currentColumnKey) {
      continue;
    }

    const values = getSelectFilterValues(filteredInfo, columnKey);

    if (values.length > 1) {
      return { hasMultipleSelections: true, selections: [] };
    }

    if (values.length === 1) {
      selections.push({
        name: columnKey,
        string: normalizeDamageSchemaStringValue(values[0]),
      });
    }
  }

  return { hasMultipleSelections: false, selections };
};

export const getDamageSchemaPrefetchSelections = ({
  columns,
  filteredInfo,
}: GetDamageSchemaPrefetchSelectionsParams): AtlasGql_DamageSchemaAttributeSelection[][] => {
  const uniqueSelections = new Map<string, AtlasGql_DamageSchemaAttributeSelection[]>();

  for (const column of columns) {
    if (column.type !== 'string' || typeof column.key !== 'string') {
      continue;
    }

    const { hasMultipleSelections, selections } = getDamageSchemaFilterState({
      columns,
      currentColumnKey: column.key,
      filteredInfo,
    });

    if (!hasMultipleSelections) {
      uniqueSelections.set(JSON.stringify(selections), selections);
    }
  }

  return Array.from(uniqueSelections.values());
};

export const shouldIncludeDamageSchemaFilterSearch = (
  columnKey: string,
  columns: DamageSchemaFilterColumn[]
): boolean =>
  SEARCHABLE_SCHEMA_FILTER_KEYS.has(columnKey) &&
  columns.some(column => column.key === columnKey && column.type === 'string');

type GetConfirmedTabListParams = {
  critical?: boolean;
  siteIds?: string[];
};

export const getConfirmedTabList = ({
  critical = false,
  siteIds,
}: GetConfirmedTabListParams): (CardTabListType & PresetFilterType)[] => [
  {
    key: 'ALL',
    tab: i18n.t('table.all', 'All'),
    value: {
      filters: {
        confirmationStatus: undefined,
        site: siteIds,
      },
      sortOrder: {
        columnKey: critical ? 'Date' : 'Severity',
        order: 'descend',
      },
    },
  },
  {
    key: 'CONFIRMED',
    tab: i18n.t('table.confirmed', 'Confirmed'),
    value: {
      filters: {
        confirmationStatus: [AtlasGqlConfirmationStatus.Confirmed],
        site: siteIds,
      },
    },
  },
  {
    key: 'UNCONFIRMED',
    tab: i18n.t('table.unconfirmed', 'Unconfirmed'),
    value: {
      filters: {
        confirmationStatus: [
          AtlasGqlConfirmationStatus.NotConfirmed,
          AtlasGqlConfirmationStatus.ConfirmedUpdated,
        ],
        site: siteIds,
      },
    },
  },
];

export const getInitialPresetFilter = (
  params: GetConfirmedTabListParams & {
    defaultActiveTabKey?: string;
    initialFilters?: Record<string, unknown>;
  }
) => {
  const { defaultActiveTabKey = 'ALL', initialFilters, ...tabListParams } = params;
  const presetFilter = getConfirmedTabList(tabListParams).find(
    tab => tab.key === defaultActiveTabKey
  );

  return presetFilter && initialFilters
    ? {
        ...presetFilter,
        value: {
          ...presetFilter.value,
          filters: { ...presetFilter.value?.filters, ...initialFilters },
        },
      }
    : presetFilter;
};

/**
 * Removes the persisted per-customer state (sort/filter/pagination) for the Inspections
 * Damage table 2.0 list, so table state does not leak between orgs when switching customers
 * or logging out.
 */
export const clearDamageListTable2 = () => {
  const { remove } = tableStorage({ storageKey: DAMAGE_LIST_TABLE_ID });
  remove();
};

export type Damages2CustomViewColumnInput = {
  key?: string | number;
  name?: string;
  visible?: boolean;
  className?: string;
  type?: string;
  config?: { type?: string };
};

// The report service matches table columns by key and attributes by name; type sets colouring.
export type Damages2CustomViewColumn = {
  key: string;
  order: number;
  name?: string;
  type?: string;
};

const toOptionalString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

// The columns DataTable renders, in the same order.
export const getDamages2CustomViewColumns = ({
  columnDefs,
  customizedColumnSet,
}: {
  columnDefs: Damages2CustomViewColumnInput[];
  customizedColumnSet?: unknown[];
}): Damages2CustomViewColumn[] =>
  getShownColumns(columnDefs, customizedColumnSet)
    .filter(column => !isHiddenColumn(column))
    .flatMap(column => {
      const key = toOptionalString(column.key);
      const name = toOptionalString(column.name);
      const type = toOptionalString(column.config?.type) ?? toOptionalString(column.type);

      return key ? [{ key, ...(name && { name }), ...(type && { type }) }] : [];
    })
    .map((column, order) => ({ ...column, order }));

export const getDamages2CustomViewVisibleColumnsArg = ({
  columnDefs,
  customizedColumnSet,
}: {
  columnDefs: Damages2CustomViewColumnInput[];
  customizedColumnSet?: unknown[];
}): { key: string; value: string } => ({
  key: VISIBLE_COLUMNS_REPORT_ARG_KEY,
  value: JSON.stringify(getDamages2CustomViewColumns({ columnDefs, customizedColumnSet })),
});

// The same sortBy the table query sends: header sort, then multi-sort, deduped by key.
export const getDamages2CustomViewSortByArg = ({
  sortBy,
  multiSortBy,
}: {
  sortBy: TQuerySort[];
  multiSortBy: TQuerySort[];
}): { key: string; value: string } => ({
  key: SORT_BY_REPORT_ARG_KEY,
  value: JSON.stringify(resolveSortBy(sortBy, multiSortBy)),
});
